import React, { useMemo, useState } from 'react';
import { Check, X, User, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';

function PendingStudentsList({
  students,
  count,
  isLoading,
  onApprove,
  onReject,
  viewAllTo = '/admin/pending-students',
  maxToShow = 5,
}) {
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const visibleStudents = useMemo(() => {
    if (!Array.isArray(students)) return [];
    return students.slice(0, maxToShow);
  }, [students, maxToShow]);

  const handleApprove = async (id) => {
    if (!onApprove) return;
    try {
      setActionLoadingId(id);
      await onApprove(id);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (id) => {
    if (!onReject) return;
    try {
      setActionLoadingId(id);
      await onReject(id);
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 'var(--spacing-lg)' }}>
        <div>
          <h3 className="section-title" style={{ marginBottom: 0 }}>Pending Students ({count ?? 0})</h3>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
            Review and approve or reject pending student registrations.
          </div>
        </div>

        <Link to={viewAllTo} className="ux-btn-sm ux-btn-ghost" style={{ textDecoration: 'none' }}>
          View all
        </Link>
      </div>

      {isLoading ? (
        <ListLoadingState label="Loading pending students…" />
      ) : visibleStudents.length === 0 ? (
        <ListEmptyState icon={User} title="No pending students" description="New registrations will appear here." />
      ) : (
        <DataRowList>
          {visibleStudents.map((s, index) => {
            const fullName = s.first_name || s.last_name
              ? `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim()
              : (s.username || 'Student');
            const email = s.email || s.username || '';

            return (
              <DataRow
                key={s.id}
                index={index}
                leading={(
                  <div className="ux-avatar">
                    <User size={16} />
                  </div>
                )}
                title={fullName}
                subtitle={(
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Mail size={12} /> {email}
                  </span>
                )}
                meta={<span>{s.campus_name || (s.campus?.name ?? '—')}</span>}
                badges={<StatusPill status="pending" label="Pending" />}
                actions={(
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button
                      type="button"
                      className="ux-icon-btn danger"
                      onClick={() => handleReject(s.id)}
                      disabled={actionLoadingId === s.id}
                      title="Reject"
                    >
                      <X size={16} />
                    </button>
                    <button
                      type="button"
                      className="ux-icon-btn success"
                      onClick={() => handleApprove(s.id)}
                      disabled={actionLoadingId === s.id}
                      title="Approve"
                    >
                      <Check size={16} />
                    </button>
                  </div>
                )}
              />
            );
          })}
        </DataRowList>
      )}
    </section>
  );
}

export default PendingStudentsList;
